import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { sendTransactionSync, type SendTransactionSyncParameters } from '@wagmi/core'
import { Rpc } from 'accounts'
import { Hex } from 'ox'
import { useState } from 'react'
import { Box, Button, Empty, Identicon, Rows, Token } from 'regen-ui'
import type { Address } from 'viem'
import { formatUnits, maxUint256 } from 'viem'
import { Actions } from 'viem/tempo'
import { useConnection } from 'wagmi'
import AppWindow from '~icons/lucide/app-window'
import CircleAlert from '~icons/lucide/circle-alert'
import CirclePlus from '~icons/lucide/circle-plus'
import RefreshCw from '~icons/lucide/refresh-cw'
import ShieldQuestion from '~icons/lucide/shield-question'
import Terminal from '~icons/lucide/terminal'
import Trash from '~icons/lucide/trash-2'

import * as Auth from '#/lib/auth.js'
import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { wagmiConfig } from '#/lib/config.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'
import { TopUpPanel } from '#/routes/_app/-components/TopUpPanel.js'
import type { AuthorizedApp, Key, LimitEntry } from '../../../worker/authorized-apps.js'

export const Route = createFileRoute('/_app/apps')({
  component: AppsPage,
})

type ChainId = (typeof wagmiConfig)['chains'][number]['id']

function AppsPage() {
  const account = Auth.useSessionAddress()
  const { address, chainId } = useConnection()
  const chainId_config = isChainId(chainId) ? chainId : undefined

  const apps = AuthorizedApps.useAuthorizedApps({
    account: account ?? null,
    chainId: chainId_config,
  })

  const body = (() => {
    if (apps.isLoading || chainId_config == null)
      return (
        <Rows variant="surface">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </Rows>
      )
    if (!account)
      return (
        <Empty icon={<ShieldQuestion />} title="Sign in to view authorized apps">
          Connected apps and CLI keys are scoped to your wallet session.
        </Empty>
      )
    if (apps.isError)
      return (
        <Empty
          action={
            <Button
              icon={<RefreshCw className="size-3.5" />}
              onClick={() => apps.refetch()}
              size="small"
              variant="secondary"
            >
              Retry
            </Button>
          }
          icon={<CircleAlert />}
          title="Couldn't load authorized apps"
        >
          Refresh this list to check the latest authorized apps.
        </Empty>
      )
    if (!apps.data || apps.data.length === 0)
      return (
        <Empty icon={<AppWindow />} title="No authorized apps yet">
          Connected apps and CLI keys will show up here with their spending limits.
        </Empty>
      )
    return (
      <Rows variant="surface">
        {apps.data.map((app, i) => (
          <AppRow
            accountAddress={address}
            app={app}
            chainId={chainId_config}
            key={app.id ?? `unknown-${i}`}
          />
        ))}
      </Rows>
    )
  })()

  return (
    <ScreenLayout
      heading={<h1 className="text-heading-24">Authorized Apps</h1>}
      walletActions={false}
    >
      <Box className="flex flex-col" padding={0}>
        {body}
      </Box>
    </ScreenLayout>
  )
}

function isChainId(chainId: number | undefined): chainId is ChainId {
  return wagmiConfig.chains.some((chain) => chain.id === chainId)
}

function AppRow(props: {
  accountAddress: Address | undefined
  app: AuthorizedApp
  chainId: ChainId
}) {
  const { accountAddress, app, chainId } = props
  const [expanded, setExpanded] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const isUnknown = app.id === null
  const connector = useDialogConnector()
  const queryClient = useQueryClient()

  const isCli = AuthorizedApps.isCliHostname(app.hostname)
  const title = app.title ?? app.hostname ?? 'Unknown App'
  const hasDetails = app.keys.length > 0
  const subtitle = (() => {
    if (isUnknown) return 'Authorized outside this wallet'
    // The synthetic CLI hostname is internal — no need to repeat it.
    if (isCli) return ''
    return app.hostname ?? ''
  })()

  // Revoke all keys for this app sequentially. Each call opens the passkey
  // prompt via the dialog connector, same as the single-key revoke flow.
  const disconnectAll = useMutation({
    mutationFn: async () => {
      if (!accountAddress) return
      const provider = await connector.getProvider()
      for (const key of app.keys) {
        await provider.request({
          method: 'wallet_revokeAccessKey',
          params: [{ accessKeyAddress: key.address, address: accountAddress }],
        } as never)
      }
      await queryClient.invalidateQueries({
        queryKey: AuthorizedApps.queryKey(accountAddress, { chainId }),
        refetchType: 'active',
      })
    },
    onSettled: () => setConfirmDisconnect(false),
  })

  const footer = (
    <div className="flex items-center justify-end gap-3 px-5 py-2.5">
      {confirmDisconnect ? (
        <>
          <span className="flex-1 text-label-13 text-foreground-secondary">
            Revoke {app.keys.length === 1 ? '1 key' : `all ${app.keys.length} keys`} for {title}?
          </span>
          <Button onClick={() => setConfirmDisconnect(false)} size="small" variant="secondary">
            Cancel
          </Button>
          <Button
            loading={disconnectAll.isPending}
            onClick={() => disconnectAll.mutate()}
            size="small"
            variant="negative"
          >
            Disconnect
          </Button>
        </>
      ) : (
        <Button
          disabled={app.keys.length === 0}
          onClick={() => setConfirmDisconnect(true)}
          size="small"
          variant="secondary"
        >
          Disconnect {title}
        </Button>
      )}
    </div>
  )
  const details = (
    <>
      <Rows.Group>
        {app.keys.map((key) => (
          <KeyEntry
            accountAddress={accountAddress}
            chainId={chainId}
            hostname={app.hostname}
            key={`${app.id ?? 'unknown'}-${key.address}`}
            keyData={key}
          />
        ))}
      </Rows.Group>
      <Rows.Panel>{footer}</Rows.Panel>
    </>
  )

  const content = (
    <>
      <AppIcon app={app} />
      <span className="flex flex-1 flex-col truncate">
        <span className="truncate text-label-14 text-foreground">{title}</span>
        {subtitle && (
          <span className="truncate text-label-13 text-foreground-secondary">{subtitle}</span>
        )}
      </span>
      <AggregateSummary keys={app.keys} limits={app.limits} />
    </>
  )

  if (hasDetails)
    return (
      <>
        <Rows.Row
          action={{ type: 'expand', expanded }}
          aria-label={`Toggle details for ${title}`}
          height={64}
          onClick={() => setExpanded((open) => !open)}
        >
          {content}
        </Rows.Row>
        {expanded && details}
      </>
    )

  return <Rows.Row height={64}>{content}</Rows.Row>
}

function AppIcon(props: { app: AuthorizedApp }) {
  const { app } = props
  const [iconFailed, setIconFailed] = useState(false)
  // Unknown app: generic shield icon.
  if (app.id === null)
    return (
      <span className="flex size-8 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary">
        <ShieldQuestion className="size-4" />
      </span>
    )
  // CLI authorization: terminal icon (no scraped icon possible).
  if (AuthorizedApps.isCliHostname(app.hostname))
    return (
      <span className="flex size-8 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary">
        <Terminal className="size-4" />
      </span>
    )
  // Known app: scraped icon → first key Identicon fallback.
  if (app.icon && !iconFailed)
    return (
      <img
        alt=""
        className="size-8 shrink-0 rounded-identicon bg-secondary object-contain"
        loading="lazy"
        onError={() => setIconFailed(true)}
        src={app.icon}
      />
    )
  const firstKey = app.keys[0]
  if (firstKey)
    return <Identicon address={firstKey.address} className="size-8 shrink-0" size={32} />
  return <span className="size-8 shrink-0 rounded-identicon bg-secondary" />
}

/** Collapsed-row summary chip showing aggregate limits. "Unlimited" when no entries. */
function AggregateSummary(props: {
  keys: readonly Key[]
  limits: readonly LimitEntry[] | undefined
}) {
  const { keys, limits = [] } = props
  // No keys have limits → "Unlimited" (empty list = no aggregate rows).
  if (limits.length === 0 && keys.length > 0)
    return <span className="text-label-13 text-foreground">Unlimited</span>
  if (limits.length === 0) return null
  // Render no-period (lifetime) entries first, then period entries, joined by " + ".
  const ordered = [...limits].sort((a, b) => Number(a.period != null) - Number(b.period != null))
  const visible = ordered.slice(0, 2)
  const overflow = ordered.slice(2)
  // Plain-text summary of hidden entries — surfaced as a tooltip on the +N chip.
  const overflowTitle = overflow
    .map((l) => {
      const formatted = formatAmount(BigInt(l.limit), l)
      return l.period != null ? `${formatted} / ${periodDisplay(l.period)}` : formatted
    })
    .join(', ')
  return (
    <span className="flex items-center gap-2 text-label-13 text-foreground tabular-nums">
      {visible.map((l, i) => (
        <span className="flex items-center gap-2" key={`${l.currency ?? l.token}-${l.period}`}>
          {i > 0 && (
            <span aria-hidden className="text-foreground-secondary">
              +
            </span>
          )}
          <LimitText entry={l} />
        </span>
      ))}
      {overflow.length > 0 && (
        <span className="text-foreground-secondary" title={overflowTitle}>
          +{overflow.length}
        </span>
      )}
    </span>
  )
}

/**
 * One entry per access key. Renders a row per limit (or a single "Unlimited"
 * row for keys with no limits) with actions for top-up and revoke flows.
 */
function KeyEntry(props: {
  accountAddress: Address | undefined
  chainId: ChainId
  hostname: string | null
  keyData: Key
}) {
  const { accountAddress, chainId, hostname, keyData: key } = props
  const connector = useDialogConnector()
  const queryClient = useQueryClient()
  const [topUpOpen, setTopUpOpen] = useState(false)

  // Open the wallet dialog at `wallet_revokeAccessKey` via the dialog
  // connector — same flow an app would trigger through the SDK.
  const revoke = useMutation({
    mutationFn: async () => {
      if (!accountAddress) return
      const provider = await connector.getProvider()
      await provider.request({
        method: 'wallet_revokeAccessKey',
        params: [{ accessKeyAddress: key.address, address: accountAddress }],
      } as never)
      // Wait for the active list to refetch before resolving so the row
      // disappears (and the button stops being clickable) atomically.
      if (accountAddress)
        await queryClient.invalidateQueries({
          queryKey: AuthorizedApps.queryKey(accountAddress, { chainId }),
          refetchType: 'active',
        })
    },
  })

  const topUp = useMutation({
    mutationFn: async (values: TopUpPanel.Values) => {
      if (!accountAddress) return
      const calls = values.limits.map((limit) =>
        Actions.accessKey.updateLimit.call({
          accessKey: key.address,
          limit: limit.limit,
          token: limit.token,
        }),
      )
      const request = {
        calls,
        chainId,
        type: 'tempo',
      } satisfies SendTransactionSyncParameters<typeof wagmiConfig, typeof chainId>
      await sendTransactionSync(wagmiConfig, request)
      const params = Rpc.wallet_authorizeAccessKey.parameters.parse({
        address: key.address,
        chainId: Hex.fromNumber(chainId),
        expiry: key.expiry,
        keyType: key.keyType,
        limits: values.limits.map((limit) => ({
          limit: Hex.fromNumber(limit.limit),
          token: limit.token,
          ...(limit.period != null ? { period: limit.period } : {}),
        })),
        ...(key.scopes ? { scopes: key.scopes } : {}),
      })
      await AuthorizedApps.upsert.strict({
        chainId,
        hostname,
        key: params,
        updateExisting: true,
      })
      await queryClient.invalidateQueries({
        queryKey: AuthorizedApps.queryKey(accountAddress, { chainId }),
        refetchType: 'active',
      })
    },
    onSuccess: () => setTopUpOpen(false),
  })

  const topUpButton =
    key.limits.length > 0 && key.state === 'active' ? (
      <Button.Icon
        aria-label="Top up access key"
        disabled={revoke.isPending || topUp.isPending}
        onClick={() => setTopUpOpen((open) => !open)}
        size="md"
        title="Top up access key"
        type="button"
        variant="plain"
      >
        <CirclePlus />
      </Button.Icon>
    ) : null

  const revokeButton = (
    <Button.Icon
      aria-label="Revoke"
      disabled={revoke.isPending || topUp.isPending}
      onClick={() => revoke.mutate()}
      size="md"
      type="button"
      variant="plain"
    >
      <Trash />
    </Button.Icon>
  )
  const actions = (
    <span className="flex items-center gap-1">
      {topUpButton}
      {revokeButton}
    </span>
  )

  if (key.limits.length === 0)
    return (
      <Rows.Row height={52}>
        <span className="flex size-7 shrink-0 items-center justify-center rounded-identicon bg-secondary">
          <Identicon address={key.address} className="size-7" size={28} />
        </span>
        <span className="flex flex-1 flex-col gap-0.5 truncate">
          <span className="truncate text-label-13 text-foreground">Unlimited</span>
          <span className="flex items-center gap-1.5 text-label-12 text-foreground-secondary">
            <ExpiryLabel expiry={key.expiry} />
          </span>
        </span>
        {actions}
      </Rows.Row>
    )

  return (
    <>
      {key.limits.map((limit, i) => (
        <Rows.Row height={52} key={`${key.address}-${limit.token}-${limit.period}-${i}`}>
          <Token address={limit.token} chainId={chainId} className="size-7 shrink-0" />
          <span className="flex flex-1 flex-col gap-0.5 truncate">
            <span className="truncate text-label-13 text-foreground">{limit.symbol}</span>
            <span className="flex items-center gap-1.5 text-label-12 text-foreground-secondary">
              <ExpiryLabel expiry={key.expiry} />
            </span>
          </span>
          <span className="flex flex-col items-end gap-0.5 tabular-nums">
            <span className="text-label-13 text-foreground">
              <LimitText entry={limit} />
            </span>
            <RemainingText entry={limit} />
          </span>
          {actions}
        </Rows.Row>
      ))}
      {topUpOpen && (
        <TopUpPanel
          error={topUp.error?.message}
          keyData={key}
          onCancel={() => setTopUpOpen(false)}
          onSubmit={(values) => topUp.mutate(values)}
          submitting={topUp.isPending}
        />
      )}
    </>
  )
}

/** Renders "<remaining> remaining" beneath a limit. Hidden when `remaining` is null (pending/fetch failure) or unlimited. */
function RemainingText(props: { entry: LimitEntry }) {
  const { entry } = props
  if (entry.remaining == null) return null
  const limitBig = BigInt(entry.limit)
  if (limitBig >= maxUint256 / 2n) return null
  const remainingBig = BigInt(entry.remaining)
  return (
    <span className="text-label-12 text-foreground-secondary">
      {formatAmount(remainingBig, entry)} remaining
    </span>
  )
}

/** Renders a `<limit> / <period_display>` chunk (no period → bare amount). */
function LimitText(props: { entry: LimitEntry }) {
  const { entry } = props
  const limitBig = BigInt(entry.limit)
  if (limitBig >= maxUint256 / 2n) return <span>Unlimited</span>
  const formatted = formatAmount(limitBig, entry)
  if (entry.period == null) return <span>{formatted}</span>
  return (
    <span>
      {formatted}
      <span className="text-foreground-secondary"> / {periodDisplay(entry.period)}</span>
    </span>
  )
}

/** Format `amount` in `entry.decimals` as either fiat (when `currency`) or crypto. */
function formatAmount(amount: bigint, entry: LimitEntry) {
  const formatted = formatUnits(amount, entry.decimals)
  const value = Number.parseFloat(formatted)
  // `Intl.NumberFormat` only accepts ISO 4217 currency codes. On testnets
  // we sometimes get token symbols (e.g. `USDC`) in `entry.currency` —
  // fall back to a plain compact number with the code as a suffix.
  if (entry.currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        currency: entry.currency,
        maximumFractionDigits: 2,
        notation: 'compact',
        style: 'currency',
      }).format(value)
    } catch {
      const compact = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        notation: 'compact',
      }).format(value)
      return `${compact} ${entry.currency}`
    }
  }
  // Crypto: short format + symbol.
  const compact = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 4,
    notation: 'compact',
  }).format(value)
  return `${compact} ${entry.symbol}`
}

/** Map a period in seconds to a short unit display: `s`, `m`, `hr`, `day`, `wk`, `mo`, `y`. */
function periodDisplay(period: number) {
  if (period >= 31_536_000) return `${roundUnit(period, 31_536_000)}y`
  if (period >= 2_592_000) return `${roundUnit(period, 2_592_000)}mo`
  if (period >= 604_800) return `${roundUnit(period, 604_800)}wk`
  if (period >= 86_400) return `${roundUnit(period, 86_400)}day`
  if (period >= 3_600) return `${roundUnit(period, 3_600)}hr`
  if (period >= 60) return `${roundUnit(period, 60)}m`
  return `${period}s`
}

function roundUnit(period: number, unit: number) {
  const n = period / unit
  return n === 1 ? '' : `${Math.round(n * 10) / 10}`
}

function SkeletonRow() {
  return (
    <Rows.Row height={64}>
      <div className="size-8 animate-pulse rounded-identicon bg-skeleton" />
      <div className="flex flex-1 flex-col gap-1">
        <div className="h-3.5 w-32 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-24 animate-pulse rounded-body bg-skeleton" />
      </div>
      <div className="h-3.5 w-12 animate-pulse rounded-body bg-skeleton" />
    </Rows.Row>
  )
}

/** Render an access-key expiry. `0` → "No expiry", past → "Expired", else "Expires on <date>". */
function ExpiryLabel(props: { expiry: number }) {
  const { expiry } = props
  if (expiry === 0) return <span>No expiry</span>
  const ms = expiry * 1000
  if (ms <= Date.now()) return <span>Expired</span>
  return (
    <>
      <span>Expires on</span>
      <span className="tabular-nums">{formatDate(new Date(ms))}</span>
    </>
  )
}

function formatDate(date: Date) {
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
}
